import {useState} from "react";
import {Autocomplete, Box, TextField, Typography} from "@mui/material";
import {useFacilityOptions} from "../../hooks/useFacilityOptions";

const labelSx = {fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.5};

// Facility picker shared by the Create User wizard's Step 4 and the User detail screen's
// Additional Details. Edit mode is a searchable field that adds facilities one at a time from the
// whole facility catalog for the selected business unit — no paging — with each addition appearing
// as a removable pill below the field. View mode is a read-only list of the assigned facility
// names. The catalog is only read in edit mode. Selection shape mirrors MultiTerritoryPicker: an
// array of {id, name} objects, so the review/view surfaces can show names without re-reading the
// catalog.
const FacilityPicker = ({editMode = true, businessUnitCode, value, onChange}) => {
    const {facilities, isError} = useFacilityOptions(businessUnitCode, editMode);
    const [inputValue, setInputValue] = useState("");

    const selected = value ?? [];
    const selectedIds = new Set(selected.map((f) => f.id));
    const availableOptions = facilities.filter((f) => !selectedIds.has(f.id));

    // ---- view mode: read-only ----
    if (!editMode) {
        return (
            <Box sx={{mt: 3}}>
                <Typography sx={labelSx}>Facilities</Typography>
                {selected.length === 0 ? (
                    <Typography sx={{fontSize: "0.9rem", color: "text.secondary", fontStyle: "italic"}}>—</Typography>
                ) : (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5}}>
                        {selected.map((f) => (
                            <Box key={f.id} sx={{px: 1.25, py: 0.5, borderRadius: "99px", border: 1, borderColor: "divider", fontSize: "0.78rem"}}>
                                {f.name}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    }

    const handleAdd = (option) => {
        if (!option) return;
        onChange([...selected, option]);
        setInputValue("");
    };

    const handleRemove = (id) => onChange(selected.filter((f) => f.id !== id));

    return (
        <Box sx={{mt: 3, border: 1, borderColor: "divider", borderRadius: "8px", p: 2}}>
            <Typography sx={labelSx}>Facilities</Typography>
            <Autocomplete
                size="small"
                blurOnSelect
                disabled={isError}
                options={availableOptions}
                value={null}
                inputValue={inputValue}
                onInputChange={(event, newInputValue, reason) => setInputValue(reason === "reset" ? "" : newInputValue)}
                onChange={(event, option) => handleAdd(option)}
                getOptionLabel={(option) => option.name ?? ""}
                isOptionEqualToValue={(option, other) => option.id === other.id}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        {option.name}
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        inputProps={{...params.inputProps, placeholder: "Search facilities"}}
                        sx={{"& input::placeholder": {color: "rgba(0, 0, 0, 0.38)", opacity: 1}}}
                    />
                )}
            />
            <Box sx={{borderTop: 1, borderColor: "divider", pt: 2, mt: 2}}>
                <Typography sx={{...labelSx, mb: 1.5}}>
                    Selected Facilities {selected.length > 0 ? `(${selected.length})` : ""}
                </Typography>
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                    {selected.length === 0 ? (
                        <Typography sx={{fontSize: "0.8rem", color: "text.secondary", fontStyle: "italic"}}>
                            No facilities selected yet
                        </Typography>
                    ) : (
                        selected.map((f) => (
                            <Box
                                key={f.id}
                                sx={{
                                    display: "inline-flex", alignItems: "center", gap: 0.5, pl: 1.25, pr: 0.5, py: 0.5,
                                    borderRadius: "99px", border: 1, borderColor: "divider", fontSize: "0.78rem",
                                }}
                            >
                                {f.name}
                                <Box
                                    component="button"
                                    onClick={() => handleRemove(f.id)}
                                    aria-label={`Remove ${f.name}`}
                                    sx={{
                                        width: 16, height: 16, borderRadius: "50%", border: "none", cursor: "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: "0.6rem", lineHeight: 1, p: 0,
                                        backgroundColor: "rgba(0,0,0,0.06)", color: "text.secondary",
                                        "&:hover": {backgroundColor: "error.main", color: "white"},
                                    }}
                                >
                                    ✕
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            </Box>
            {isError && (
                <Typography sx={{fontSize: "0.8rem", color: "text.secondary", mt: 0.5}}>
                    Facilities could not be loaded for this business unit.
                </Typography>
            )}
        </Box>
    );
};

export default FacilityPicker;
